import { Button } from '@affine/component';
import {
  AccountDeletionBlockReason,
  type AccountDeletionPreview,
  type WorkspacePreview,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';

import * as styles from './style.css';

export interface HardBlockViewProps {
  preview: AccountDeletionPreview;
  onClose: () => void;
}

// sl-zvjz: HARD BLOCK — single owner of a workspace with other active members.
// Backend enforces this at schedule-time too (race condition); the UI just
// surfaces the offending workspaces and tells the user how to unblock.
export const HardBlockView = ({ preview, onClose }: HardBlockViewProps) => {
  const t = useI18n();

  const blockingWorkspaces = preview.ownedWorkspaces.filter(
    (ws: WorkspacePreview) => ws.memberCount > 1
  );

  return (
    <div className={styles.dialogContent}>
      <div className={styles.stepHeader}>
        <span>
          {t['com.streamlain.setting.account.delete.hard-block.badge']()}
        </span>
      </div>

      <h2 className={styles.sectionTitle}>
        {t['com.streamlain.setting.account.delete.hard-block.title']()}
      </h2>

      <p>
        {preview.blockedReason ===
        AccountDeletionBlockReason.OWNER_OF_ACTIVE_WORKSPACE
          ? t['com.streamlain.setting.account.delete.hard-block.body-owner']()
          : t['com.streamlain.setting.account.delete.hard-block.body']()}
      </p>

      {blockingWorkspaces.length > 0 ? (
        <ul className={styles.sectionList}>
          {blockingWorkspaces.map((ws: WorkspacePreview) => (
            <li key={ws.id} className={styles.sectionListItem}>
              <span>{ws.name || ws.id}</span>
              <span className={styles.memberCount}>
                {t[
                  'com.streamlain.setting.account.delete.step-info.workspace-members'
                ]({ count: String(ws.memberCount) })}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className={styles.complianceHint}>
        <span>
          {t['com.streamlain.setting.account.delete.hard-block.hint']()}
        </span>
      </div>

      <div className={styles.footer}>
        <span />
        <Button variant="primary" onClick={onClose}>
          {t['com.streamlain.setting.account.delete.dialog.close']()}
        </Button>
      </div>
    </div>
  );
};
